import React, { Suspense } from "react";
import { StyleSheet, View } from "react-native";
import variables from "../../styles/variables";
import { NestedKeyOf } from "../../../utility";
import lazyImport from "../../utils/lazyImport/lazyImport";
import { importIcon } from "./icons";
import { getColorValue } from "../../utils/colors";
import SharedIcon from "./SharedIcon";
import { Props } from "./IconProps";

export type IconSize = keyof typeof variables.sizes;
export type Colors = typeof variables.colors;
export type IconFill = NestedKeyOf<Colors>;

// Eager load list for web.
const eagerLoadList = [
  "longLogo",
  "search",
  "profile",
  "play",
  "chevronLeft",
  "chevronRightSmall",
  "raceIqLogoTeal",
  "draw",
  "drawSelected",
  "start",
  "startSelected",
  "time",
  "timeSelected",
  "speed",
  "speedSelected",
  "stride",
  "strideSelected",
  "finish",
  "finishSelected",
  "jockey",
  "jockeySelected",
  "jumping",
  "jumpingSelected",
];

const Icon = ({
  name,
  style,
  size,
  fill,
  rotate,
  notifications = 0,
  stroke,
  color,
  onTabar = false,
  eagerLoad = false,
  ...rest
}: Props) => {
  // Check if the icon is in the eager load list or if eagerLoad is true
  if (eagerLoadList.includes(name) || eagerLoad) {
    // The backend can send icon names we don't have an SVG for (e.g. rogue
    // weather types like "mostly-cloudy"). require() of a missing file throws,
    // so guard it and render nothing rather than crashing the page.
    let IconComponent;
    try {
      IconComponent = require(`./icons/${name}.svg`).default;
    } catch {
      return null;
    }

    return (
      <SharedIcon
        key={`icon-${name}`}
        component={IconComponent}
        style={[styles.icon, style]}
        rotate={rotate}
        onTabar={onTabar}
        color={color && color}
        fill={fill && getColorValue(fill)}
        stroke={stroke && getColorValue(stroke)}
        {...(size && variables.sizes[size])}
        {...rest}
      />
    );
  }

  // Dynamic import using lazyImport
  const IconComponent = React.useMemo(() => {
    return lazyImport(() => importIcon(name));
  }, [name]);

  if (!IconComponent) return null;

  return (
    <Suspense
      fallback={
        <View
          style={{
            width: variables.sizes[size]?.width,
            height: variables.sizes[size]?.height,
          }}
        />
      }
    >
      <SharedIcon
        key={`icon-${name}`}
        component={IconComponent}
        style={[styles.icon, style]}
        rotate={rotate}
        color={color && color}
        fill={fill && getColorValue(fill)}
        stroke={stroke && getColorValue(stroke)}
        {...(size && variables.sizes[size])}
        notifications={notifications}
        onTabar={onTabar}
        {...rest}
      />
    </Suspense>
  );
};

/* don't add height and width attrs for web */
const styles = StyleSheet.create({
  icon: {
    userSelect: "none", // stops any nearby text highlighting
  },
});

export default React.memo(Icon);
